import { useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, DataTable, EmptyState, FilterBar, Segmented, Stack, t, Text } from '@kanbios/ui';
import { useEnvironmentFilter } from '../EnvironmentPicker';
import type { fleetState, maintenance } from '@kanbios/api-client';
import { api, unwrap } from '../../lib/api';
import { shortDate } from '../../lib/format';
import { environmentColumn } from '../ServerState';
import { useEnvironmentName } from '../../lib/environments';
import { healthColumn, HealthLegend } from '../StateColumn';
import { ToneView, toneViewItems } from './toneViews';
import { AttachSiteDialog } from '../AttachSiteDialog';

type Address = maintenance.components['schemas']['Address'];
type Site = fleetState.components['schemas']['Running']['sites'][number];

const TONES = { dns_elsewhere: 'warn', dns_none: 'warn', cert_expired: 'neg', cert_7: 'neg', registry_30: 'warn', cert_21: 'warn', to_attach: 'warn', not_served: 'neutral', ok: 'pos' } as const;
const SITUATIONS = Object.keys(TONES) as Address['situation'][];
const situationSpec = (s: Address['situation']) => ({ tone: TONES[s], label: t(`addresses.situation.${s}`) });

function Expiry({ daysLeft, at, warn, crit }: { daysLeft: number | null; at: string | null; warn: number; crit: number }) {
  if (daysLeft === null || !at) return <Text variant="body-sm" tone="mute">{t('addresses.unknown')}</Text>;
  if (daysLeft < 0) return <Badge tone="neg" size="sm">{t('addresses.expired', { date: shortDate(at) })}</Badge>;
  if (daysLeft < warn) return <Badge tone={daysLeft < crit ? 'neg' : 'warn'} size="sm">{t('addresses.expiresIn', { n: daysLeft })}</Badge>;
  return <Text variant="body-sm">{t('addresses.until', { date: shortDate(at) })}</Text>;
}

/** D78: « Pointe ici », « Ailleurs : 1.2.3.4 », « Aucun enregistrement », « Derrière un proxy »; the details in the tooltip. */
function DnsCell({ address }: { address: Address }) {
  if (!address.server) return <>—</>;
  const dns = address.dns;
  if (!dns) return <Text variant="body-sm" tone="mute" truncate>{t('addresses.dns.pending')}</Text>;
  const title = [dns.cname ? `CNAME ${dns.cname}` : null, dns.addresses.join(', ') || null, t('addresses.dns.checked', { when: shortDate(dns.checkedAt) })].filter(Boolean).join(' · ');
  if (dns.status === 'here') return <span title={title}><Badge tone="pos" size="sm">{t('addresses.dns.here')}</Badge></span>;
  if (dns.status === 'elsewhere') return <span title={title}><Badge tone="warn" size="sm">{t('addresses.dns.elsewhere', { address: dns.addresses[0] ?? '' })}</Badge></span>;
  if (dns.status === 'none') return <span title={title}><Badge tone="warn" size="sm">{t('addresses.situation.dns_none')}</Badge></span>;
  if (dns.status === 'proxied') return <span title={title}><Text variant="body-sm" truncate>{t('addresses.dns.proxied')}</Text></span>;
  return <span title={title}><Text variant="body-sm" tone="mute" truncate>{t('addresses.dns.unknown')}</Text></span>;
}

/**
 * D27 « Domaines »: the addresses served by the servers (SPEC-032) and the
 * ones the catalogue declares, with their project, server, certificate and the
 * registry expiry of their domain name. Given a project, its own addresses and
 * the ones still to attach on the servers it shares; « Rattacher » opens the
 * pull request of SPEC-006, the project chosen.
 */
export function AddressTable({ project, application, server, height }: { project?: string; application?: string; server?: string; height?: number }) {
  const navigate = useNavigate();
  const envName = useEnvironmentName();
  const queryClient = useQueryClient();
  const [view, setView] = useState<ToneView>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [attaching, setAttaching] = useState<Address | null>(null);
  // D73: the Adresses list follows the global environment filter; a project's, an application's or a server's tab shows all of theirs.
  const filter = useEnvironmentFilter();
  const env = project || application || server ? undefined : filter;
  const key = ['addresses', project ?? null, application ?? null, server ?? null, env ?? null, view, search, page];
  const list = useQuery({
    queryKey: key,
    queryFn: async () => unwrap(await api.maintenance.GET('/addresses', { params: { query: { view, q: search || undefined, project, application, server, env, page, pageSize: 25 } } })),
    placeholderData: keepPreviousData,
  });
  const canAttach = Boolean(list.data?.canAttach);

  return (
    <Stack gap={16}>
      <Segmented label={t('addresses.views')} size="sm" value={view} onChange={v => { setView(v as ToneView); setPage(1); }}
        items={toneViewItems('addresses', list.data?.views)} />
      <DataTable
        height={height}
        label={t('addresses.table')}
        rows={list.data?.items ?? []}
        rowKey={(a: Address) => `${a.host}@${a.server ?? ''}`}
        manual
        totalCount={list.data?.total ?? 0}
        loading={list.isPending}
        toolbarStart={<FilterBar label={t('addresses.filters')} search={search} onSearchChange={q => { setSearch(q); setPage(1); }} searchPlaceholder={t('addresses.search')} value={{}} onChange={() => undefined} filters={[]} />}
        toolbarSearch={false}
        toolbarFilters={false}
        density="compact"
        // One line per address: its situation (D57, a dot first), the address, then where it lives and its two expiries.
        // D58: the environment in its own column, never a badge after the project's name.
        columns={[
          healthColumn<Address>({
            legendLabel: t('addresses.legend'), legendTitle: t('addresses.legendTitle'),
            legend: <HealthLegend items={SITUATIONS.map(situationSpec)} />,
            spec: a => situationSpec(a.situation),
          }),
          { key: 'host', label: t('addresses.columns.host'), mono: true, minWidth: 220, grow: true, pinned: 'left' as const },
          {
            // Within a project the project is the page: the column names the application.
            key: 'attachedTo', label: project || application ? t('addresses.columns.application') : t('addresses.columns.project'), width: 280,
            accessor: (a: Address) => (a.attachedTo ? (project || application ? a.attachedTo.application : `${a.attachedTo.clientTitle} › ${a.attachedTo.projectTitle}`) : ''),
            render: (a: Address) => (a.attachedTo ? (
              <Text variant="body-sm" truncate>{project || application ? a.attachedTo.application : `${a.attachedTo.clientTitle} › ${a.attachedTo.projectTitle}`}</Text>
            ) : canAttach && a.server
              // « À rattacher » is said by the situation: here, the action only.
              ? <Button variant="tonal" size="sm" onClick={() => setAttaching(a)} aria-label={t('addresses.attach', { host: a.host })}>{t('running.attach')}</Button>
              : '—'),
          },
          environmentColumn<Address>(t('addresses.columns.environment'), envName, a => (a.attachedTo ? { environment: a.attachedTo.environment, type: a.attachedTo.environmentType } : null)),
          // Not served: the situation says it (« Déclarée, non servie »). A server's tab is the server.
          ...(server ? [] : [{ key: 'server', label: t('addresses.columns.server'), width: 200, render: (a: Address) => (a.server
            ? <Button variant="ghost" size="sm" onClick={() => navigate(`/servers/${encodeURIComponent(a.server!)}?tab=domains`)}>{a.server}</Button>
            : '—') }]),
          // D78: what the address resolves to, against its server's IP.
          { key: 'dns', label: t('addresses.columns.dns'), width: 190, render: (a: Address) => <DnsCell address={a} /> },
          { key: 'tls', label: t('addresses.columns.certificate'), width: 160, render: (a: Address) => (a.server
            ? (a.tls ? <Expiry daysLeft={a.tls.daysLeft} at={a.tls.notAfter} warn={21} crit={7} /> : <Text variant="body-sm" tone="mute">{t('running.sites.noTls')}</Text>)
            : '—') },
          {
            // The registry expiry of the domain name; the name itself (derived from the address) in the tooltip.
            key: 'registry', label: t('addresses.columns.registry'), width: 160, render: (a: Address) => (
              <span title={a.registrable}>
                {a.registry
                  ? <Expiry daysLeft={a.registry.daysLeft} at={a.registry.expiresAt} warn={60} crit={30} />
                  : <Text variant="body-sm" tone="mute" truncate title={t('addresses.registryPending')}>{t('addresses.registryPending')}</Text>}
              </span>
            ),
          },
        ]}
        empty={<EmptyState framed={false} icon="language" title={view === 'all' ? t('addresses.empty.none') : t('addresses.empty.view', { view: t(`addresses.view.${view}`) })}>{view === 'all' ? t('addresses.empty.hint') : undefined}</EmptyState>}
        pagination={{ page, pageSize: 25, total: list.data?.total ?? 0, onPageChange: setPage }}
      />
      {attaching?.server && (
        <AttachSiteDialog
          server={attaching.server}
          site={{ domains: [attaching.host] } as Site}
          project={project ?? (attaching.candidates.length === 1 ? attaching.candidates[0]!.project : undefined)}
          onClose={() => setAttaching(null)}
          onOpened={() => { setAttaching(null); void queryClient.invalidateQueries({ queryKey: ['addresses'] }); }}
        />
      )}
    </Stack>
  );
}
